// src/pages/AccountSettings.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { signOut, updateProfile } from 'firebase/auth';
import { auth } from '../firebase';
import { 
  ArrowLeft, 
  User, 
  Users,
  CreditCard, 
  Settings, 
  Crown, 
  Calendar,
  AlertTriangle,
  ExternalLink,
  Mail,
  Phone,
  CheckCircle,
  XCircle,
  Clock,
  Sparkles,
  RefreshCw,
  Trash2,
  LogOut,
  FileText,  // ✅ for report cards
  Link,      // TEMPORARY: class link
  Copy,      // TEMPORARY: copy link button
  Pencil     // ✅ for edit buttons
} from 'lucide-react';
import { 
  COURSE_PACKAGES, 
  isCourseExpired,
  convertFirestoreDate
} from '../config/coursePackages';
import { 
  createCustomerPortal,
  removeSavedPaymentMethod
} from '../config/stripe';
import { getChildren, formatChildInfo, getRenewableChildren, isAdultAccount } from '../utils/childrenHelper';
import ProfileContactFields from '../components/ProfileContactFields';
import ProfileCompletionModal from '../components/ProfileCompletionModal';
import { isProfileComplete, validateProfileFields } from '../config/locationData';
import { isAdmin } from '../config/adminConfig';
import { TRIAL_SIGNUP_ENABLED } from '../config/featureFlags';
import { getTrialTimezone } from '../utils/trialHelper';
import { formatFriendlyDate, formatISOForDisplay } from '../utils/timezoneUtils';
import ChangePasswordSection from '../components/ChangePasswordSection';

const FUNCTIONS_BASE_URL = process.env.REACT_APP_FUNCTIONS_URL ||
  'https://us-central1-arabic-ai-kids.cloudfunctions.net';

// One-line status for a courseless trial child's account card.
//
// "Enrolled" is keyed on convertedPackageId, NOT convertedAt: the
// trial_conversion webhook sets convertedPackageId only on a completed PAYMENT,
// at which point the child gains a course (and this card — gated on !childCourse
// — stops rendering). A bare `won` status, even with convertedAt, can be
// hand-set by an admin with no payment, leaving the child course:null; calling
// that "Enrolled" would be the Phase 1 bug relocated.
function trialStatusLine(trial) {
  if (!trial) return 'Trial requested';
  if (trial.convertedPackageId) return 'Enrolled';
  switch (trial.status) {
    case 'requested':
      return 'Trial requested';
    case 'scheduled': {
      if (!trial.scheduledAt) return 'Trial booked';
      // Same helpers + fallbacks as TrialStatus's TrialCard so the two screens
      // render the identical date/time/zone string.
      const tz = getTrialTimezone(trial);
      const d = formatFriendlyDate(trial.scheduledAt, tz || 'UTC');
      const t = formatISOForDisplay(trial.scheduledAt, tz || 'UTC');
      return `Trial booked for ${d}, ${t}${tz ? ` (${tz})` : ' (UTC)'}`;
    }
    case 'held':
    case 'won':
    case 'lost':
      return 'Trial completed';
    case 'no_show':
    case 'cancelled':
    case 'expired':
      return 'Trial cancelled';
    default:
      return 'Trial requested';
  }
}

function AccountSettings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState(null);
  const [courseData, setCourseData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selectedChildIds, setSelectedChildIds] = useState([]);

  // Trial docs for this account, keyed by studentId — used only to label the
  // courseless (trial) child cards. One where('userId','==',uid) read.
  const [trialsByStudentId, setTrialsByStudentId] = useState({});

  // ✅ Report card state
  const [unreadReportCount, setUnreadReportCount] = useState(0);
  const [copiedLinkId, setCopiedLinkId] = useState(null); // TEMPORARY: class link copy feedback

  // ── Edit: Parent Name ──────────────────────────────────────────────────────
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');

  // ── Edit: Child Info ───────────────────────────────────────────────────────
  // editingChildId tracks WHICH child is open for editing (only ever one at a time).
  const [editingChildId, setEditingChildId] = useState(null);
  const [editChildName, setEditChildName] = useState('');
  const [editChildDob, setEditChildDob] = useState(''); // stored as "YYYY-MM-DD" for <input type="date">

  // Shared saving flag used by both parent-name and child-info saves
  const [isSaving, setIsSaving] = useState(false);

  // ── Edit: Contact Info ─────────────────────────────────────────────────────
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [editContact, setEditContact] = useState({
    phoneNumber: '',
    country: '',
    stateProvince: '',
    city: ''
  });
  const [contactEditErrors, setContactEditErrors] = useState({});

  useEffect(() => {
    loadUserProfile();
  }, [user]);

  // Label courseless (trial) child cards. Additive; renders nothing if the
  // account has no trials or the flag is off.
  useEffect(() => {
    if (!TRIAL_SIGNUP_ENABLED || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'trials'), where('userId', '==', user.uid)));
        if (cancelled) return;
        const map = {};
        snap.docs.forEach((d) => {
          const t = { id: d.id, ...d.data() };
          if (t.studentId) map[t.studentId] = t;
        });
        setTrialsByStudentId(map);
      } catch (err) {
        console.error('AccountSettings: failed to load trials', err);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const loadUserProfile = async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserProfile(userData);
        
        // Derive courseData from children array (primary source of truth)
        const allChildren = getChildren(userData);
        if (allChildren.length > 0) {
          // Find the most relevant child's course for the subscription panel
          const activeChild = allChildren.find(c => c.course?.status === 'active');
          const expiredChild = allChildren.find(c => c.course?.status === 'expired');
          const anyChild = allChildren[0];
          const relevantChild = activeChild || expiredChild || anyChild;
          
          if (relevantChild?.course) {
            setCourseData({
              ...relevantChild.course,
              quantity: allChildren.length
            });
          }
        } else if (userData.course) {
          // Pre-migration safety net
          setCourseData(userData.course);
        }
        
        console.log('📊 AccountSettings - Children:', {
          childrenCount: allChildren.length,
          children: allChildren.map(c => ({
            name: c.name,
            status: c.course?.status,
            packageId: c.course?.packageId
          }))
        });
      } else {
        setError('User profile not found');
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      setError('Failed to load user profile');
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Calculate unread report cards
  useEffect(() => {
    if (userProfile?.children) {
      let count = 0;
      userProfile.children.forEach(child => {
        if (child.reportCards) {
          child.reportCards.forEach(rc => {
            if (!rc.viewedAt) count++;
          });
        }
      });
      setUnreadReportCount(count);
    }
  }, [userProfile]);

  useEffect(() => {
    if (userProfile) {
      console.log('🔍 AccountSettings userProfile:', {
        payment: userProfile.payment,
        savedPaymentMethodId: userProfile.payment?.savedPaymentMethodId,
      });
    }
  }, [userProfile]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const converted = convertFirestoreDate(dateStr);
    if (!converted) return 'N/A';
    
    return converted.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const dob = convertFirestoreDate(dateOfBirth);
    if (!dob) return null;
    
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    
    return age;
  };

  // ── Edit: Parent Name Handlers ─────────────────────────────────────────────

  const handleStartEditProfile = () => {
    setEditFirstName(userProfile?.firstName || '');
    setEditLastName(userProfile?.lastName || '');
    setIsEditingProfile(true);
    setError(null);
    setSuccessMessage(null);
  };

  const handleCancelEditProfile = () => {
    setIsEditingProfile(false);
    setEditFirstName('');
    setEditLastName('');
    setError(null);
  };

  const handleSaveParentName = async () => {
    if (!editFirstName.trim() || !editLastName.trim()) {
      setError('First name and last name cannot be empty.');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      setSuccessMessage(null);

      // Update Firestore — only touch firstName and lastName
      await updateDoc(doc(db, 'users', user.uid), {
        firstName: editFirstName.trim(),
        lastName: editLastName.trim(),
      });

      // Keep Firebase Auth displayName in sync
      await updateProfile(auth.currentUser, {
        displayName: `${editFirstName.trim()} ${editLastName.trim()}`,
      });

      setIsEditingProfile(false);
      setSuccessMessage('Profile name updated successfully.');
      await loadUserProfile();
    } catch (err) {
      console.error('Error saving parent name:', err);
      setError('Failed to save name. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Edit: Contact Info Handlers ────────────────────────────────────────────

  const handleStartEditContact = () => {
    setEditContact({
      phoneNumber: userProfile?.phoneNumber || '',
      country: userProfile?.country || '',
      stateProvince: userProfile?.stateProvince || '',
      city: userProfile?.city || ''
    });
    setContactEditErrors({});
    setIsEditingContact(true);
    setError(null);
    setSuccessMessage(null);
  };

  const handleCancelEditContact = () => {
    setIsEditingContact(false);
    setContactEditErrors({});
    setError(null);
  };

  const handleContactEditChange = (update) => {
    setEditContact((prev) => ({ ...prev, ...update }));
  };

  const handleSaveContactInfo = async () => {
    const validationErrors = validateProfileFields(editContact);
    if (Object.keys(validationErrors).length > 0) {
      setContactEditErrors(validationErrors);
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      setSuccessMessage(null);

      // Update Firestore — only the four contact fields, nothing else
      await updateDoc(doc(db, 'users', user.uid), {
        phoneNumber: editContact.phoneNumber.trim(),
        country: editContact.country.trim(),
        stateProvince: editContact.stateProvince.trim(),
        city: editContact.city.trim(),
      });

      setIsEditingContact(false);
      setSuccessMessage('Contact information updated successfully.');
      await loadUserProfile();
    } catch (err) {
      console.error('Error saving contact info:', err);
      setError('Failed to save contact information. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Edit: Child Info Handlers ──────────────────────────────────────────────

  const handleStartEditChild = (child) => {
    // Convert the stored ISO date string to "YYYY-MM-DD" for <input type="date">
    let dobForInput = '';
    if (child.dateOfBirth) {
      const dob = convertFirestoreDate(child.dateOfBirth);
      if (dob) {
        const yyyy = dob.getFullYear();
        const mm = String(dob.getMonth() + 1).padStart(2, '0');
        const dd = String(dob.getDate()).padStart(2, '0');
        dobForInput = `${yyyy}-${mm}-${dd}`;
      }
    }

    setEditingChildId(child.id);
    setEditChildName(child.name || '');
    setEditChildDob(dobForInput);
    setError(null);
    setSuccessMessage(null);
  };

  const handleCancelEditChild = () => {
    setEditingChildId(null);
    setEditChildName('');
    setEditChildDob('');
    setError(null);
  };

  const handleSaveChildInfo = async (childId) => {
    if (!editChildName.trim()) {
      setError("Child's name cannot be empty.");
      return;
    }
    if (!editChildDob) {
      setError("Date of birth cannot be empty.");
      return;
    }

    // Safety check: we MUST have the full children array to write back atomically
    if (!Array.isArray(userProfile?.children)) {
      setError('Unable to update child information. Please refresh and try again.');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      setSuccessMessage(null);

      // Build a new children array where ONLY name and dateOfBirth change
      // for the matching child. Every other field on every child is preserved
      // exactly via the spread operator.
      const updatedChildren = userProfile.children.map((child) => {
        if (child.id === childId) {
          return {
            ...child,                                         // preserve ALL existing fields
            name: editChildName.trim(),                      // update name
            dateOfBirth: (() => {
              const [yyyy, mm, dd] = editChildDob.split('-').map(Number);
              return new Date(yyyy, mm - 1, dd).toISOString();
            })(), // update DOB as ISO string
          };
        }
        return child; // every other child is completely untouched
      });

      // Write the full array atomically.
      // NEVER use dot-notation like `children.${index}.name` — that corrupts
      // the Firestore array into a map structure.
      await updateDoc(doc(db, 'users', user.uid), {
        children: updatedChildren,
      });

      setEditingChildId(null);
      setEditChildName('');
      setEditChildDob('');
      setSuccessMessage('Child information updated successfully.');
      await loadUserProfile();
    } catch (err) {
      console.error('Error saving child info:', err);
      setError('Failed to save child information. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Existing Handlers ──────────────────────────────────────────────────────

  const handleManageBilling = async () => {
    try {
      setIsProcessing(true);
      setError(null);

      const portalUrl = await createCustomerPortal(
        user.uid,
        window.location.origin + '/account'
      );

      window.location.href = portalUrl;
    } catch (err) {
      console.error('Error opening customer portal:', err);
      setError('Failed to open billing portal. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleRemovePaymentMethod = async () => {
    if (!window.confirm('Are you sure you want to remove your saved payment method? You can always add it back during your next renewal.')) {
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);
      setSuccessMessage(null);

      await removeSavedPaymentMethod(user.uid);
      
      setSuccessMessage('Payment method removed successfully');
      
      await loadUserProfile();
    } catch (err) {
      console.error('Error removing payment method:', err);
      setError('Failed to remove payment method. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleToggleChildSelection = (childId) => {
    setSelectedChildIds(prev => {
      if (prev.includes(childId)) {
        return prev.filter(id => id !== childId);
      } else {
        return [...prev, childId];
      }
    });
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // ✅ Handle report card download and mark as viewed
  const handleDownloadReport = async (childId, reportCardId, fileUrl) => {
    try {
      // Mark as viewed in Firestore
      await fetch(`${FUNCTIONS_BASE_URL}/markReportCardAsViewed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          childId,
          reportCardId
        })
      });
      
      // Refresh user profile to update unread count
      await loadUserProfile();
    } catch (error) {
      console.error('Error marking report as viewed:', error);
    }
    
    // Open file in new tab
    window.open(fileUrl, '_blank');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-purple-600 text-xl font-bold">Loading your account...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-4">
      {/* Contact-info gate: blocks the account page until required fields are present.
          Staff are exempt; already-complete profiles never see this. */}
      {userProfile && !isAdmin(user?.uid) && !isProfileComplete(userProfile) && (
        <ProfileCompletionModal userData={userProfile} onComplete={loadUserProfile} />
      )}
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/')}
            className="p-3 bg-white/20 hover:bg-white/30 text-white rounded-xl transition-all duration-200 backdrop-blur-sm"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-3xl font-bold text-white">Account Hub</h1>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Profile Information */}
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl">
                <User size={24} />
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Profile Information</h2>
            </div>

            <div className="space-y-4">
              {/* Email — read-only */}
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                <Mail className="text-gray-500" size={20} />
                <div>
                  <div className="font-semibold text-gray-800">{user?.email}</div>
                  <div className="text-sm text-gray-600">Email Address</div>
                </div>
              </div>

              {/* Parent Name — editable */}
              {(userProfile?.firstName || userProfile?.lastName) && (
                <div className="p-4 bg-gray-50 rounded-xl">
                  {isEditingProfile ? (
                    /* ── Edit mode ── */
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">
                            First Name
                          </label>
                          <input
                            type="text"
                            value={editFirstName}
                            onChange={(e) => setEditFirstName(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm bg-white"
                            placeholder="First name"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">
                            Last Name
                          </label>
                          <input
                            type="text"
                            value={editLastName}
                            onChange={(e) => setEditLastName(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm bg-white"
                            placeholder="Last name"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveParentName}
                          disabled={isSaving}
                          className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          {isSaving ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <CheckCircle size={16} />
                          )}
                          Save
                        </button>
                        <button
                          onClick={handleCancelEditProfile}
                          disabled={isSaving}
                          className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* ── View mode ── */
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <User className="text-gray-500" size={20} />
                        <div>
                          <div className="font-semibold text-gray-800">
                            {userProfile.firstName} {userProfile.lastName}
                          </div>
                          <div className="text-sm text-gray-600">Full Name</div>
                        </div>
                      </div>
                      <button
                        onClick={handleStartEditProfile}
                        className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        title="Edit name"
                      >
                        <Pencil size={16} />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Contact Information — editable */}
              <div className="p-4 bg-gray-50 rounded-xl">
                {isEditingContact ? (
                  /* ── Edit mode ── */
                  <div className="space-y-3">
                    <ProfileContactFields
                      values={editContact}
                      onChange={handleContactEditChange}
                      errors={contactEditErrors}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveContactInfo}
                        disabled={isSaving}
                        className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {isSaving ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <CheckCircle size={16} />
                        )}
                        Save
                      </button>
                      <button
                        onClick={handleCancelEditContact}
                        disabled={isSaving}
                        className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ── View mode ── */
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <Phone className="text-gray-500 mt-0.5" size={20} />
                      <div>
                        <div className="font-semibold text-gray-800">
                          {userProfile?.phoneNumber || 'Not provided'}
                        </div>
                        <div className="text-sm text-gray-600">
                          {[userProfile?.city, userProfile?.stateProvince, userProfile?.country]
                            .filter(Boolean)
                            .join(', ') || 'Location not provided'}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={handleStartEditContact}
                      className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                      title="Edit contact information"
                    >
                      <Pencil size={16} />
                    </button>
                  </div>
                )}
              </div>

              <ChangePasswordSection user={user} />
              {/* Children Information with Course Details */}
              {getChildren(userProfile).length > 0 && (
                <div className="border-t border-gray-200 pt-6 mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">
                      {isAdultAccount(userProfile)
                        ? 'Your Subscription'
                        : getChildren(userProfile).length === 1
                          ? 'Child Information'
                          : 'Children Information'}
                    </h3>
                  </div>
                  
                  <div className="space-y-4">
                    {getChildren(userProfile).map((child) => {
                      const childCourse = child.course;
                      const isRenewable = childCourse &&
                        (childCourse.status === 'active' ||
                         childCourse.status === 'expired' ||
                         childCourse.status === 'completed');
                      const isExpired = childCourse && isCourseExpired(childCourse.endDate);
                      const isEditingThisChild = editingChildId === child.id;
                      
                      return (
                        <div 
                          key={child.id} 
                          className={`p-5 bg-gradient-to-br from-white to-gray-50 rounded-xl border-2 ${
                            isRenewable ? 'border-purple-200' : 'border-gray-200'
                          }`}
                        >
                          {/* Child Header */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              {isRenewable && !isEditingThisChild && (
                                <input
                                  type="checkbox"
                                  checked={selectedChildIds.includes(child.id)}
                                  onChange={() => handleToggleChildSelection(child.id)}
                                  className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                                />
                              )}
                              <div>
                                {isEditingThisChild ? (
                                  /* ── Child edit mode ── */
                                  <div className="space-y-3 min-w-[220px]">
                                    <div>
                                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                                        Child's Name
                                      </label>
                                      <input
                                        type="text"
                                        value={editChildName}
                                        onChange={(e) => setEditChildName(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm bg-white"
                                        placeholder="Child's full name"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                                        Date of Birth
                                      </label>
                                      <input
                                        type="date"
                                        value={editChildDob}
                                        onChange={(e) => setEditChildDob(e.target.value)}
                                        max={new Date().toISOString().split('T')[0]}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm bg-white"
                                      />
                                    </div>
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => handleSaveChildInfo(child.id)}
                                        disabled={isSaving}
                                        className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                      >
                                        {isSaving ? (
                                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                          <CheckCircle size={16} />
                                        )}
                                        Save
                                      </button>
                                      <button
                                        onClick={handleCancelEditChild}
                                        disabled={isSaving}
                                        className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  /* ── Child view mode ── */
                                  <>
                                    <div className="font-bold text-gray-800 text-lg">{child.name}</div>
                                    <div className="text-sm text-gray-600">
                                      Age: {calculateAge(child.dateOfBirth) || 'N/A'} • Born {formatDate(child.dateOfBirth)}
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {/* Edit child button — only shown in view mode */}
                              {!isEditingThisChild && (
                                <button
                                  onClick={() => handleStartEditChild(child)}
                                  className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                  title="Edit child info"
                                >
                                  <Pencil size={16} />
                                </button>
                              )}

                              {/* Course Status Badge — hidden during edit to avoid clutter */}
                              {childCourse && !isEditingThisChild && (
                                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                  childCourse.status === 'active'    ? 'bg-green-100 text-green-800' :
                                  childCourse.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                  childCourse.status === 'expired'   ? 'bg-red-100 text-red-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {childCourse.status === 'active'    && '✓ Active'}
                                  {childCourse.status === 'completed' && '🎉 Course Complete'}
                                  {childCourse.status === 'expired'   && '✗ Expired'}
                                  {childCourse.status === 'pending'   && '⏳ Pending'}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Course Details — hidden during child edit */}
                          {!isEditingThisChild && childCourse && childCourse.packageId && (
                            <div className="mt-3 space-y-2 text-sm">
                              <div className="flex items-center gap-2 text-gray-700">
                                <Crown size={16} className="text-purple-500" />
                                <span className="font-medium">
                                  {COURSE_PACKAGES[childCourse.packageId]?.name || 'Course Package'}
                                </span>
                              </div>
                              
                              {/* {childCourse.startDate && (
                                <div className="flex items-center gap-2 text-gray-600">
                                  <Calendar size={16} />
                                  <span>
                                    Started: {formatDate(childCourse.startDate)}
                                  </span>
                                </div>
                              )} */}
                              
                              {/* {childCourse.endDate && (
                                <div className="flex items-center gap-2 text-gray-600">
                                  <Calendar size={16} />
                                  <span>
                                    {isExpired ? 'Ended' : 'Ends'}: {formatDate(childCourse.endDate)}
                                  </span>
                                </div>
                              )} */}
                            </div>
                          )}

                          {/* ── Trial child (course: null) ──────────────────────
                               Additive to the courseless branch ONLY — a child
                               WITH a course never reaches here. Flag-gated.
                          ──────────────────────────────────────────────────── */}
                          {!isEditingThisChild && TRIAL_SIGNUP_ENABLED && !childCourse && (() => {
                            const trial = trialsByStudentId[child.id];
                            // Enrol only when there's a real trial doc that hasn't
                            // been paid. No trial = an orphan (its trial was
                            // deleted "trial only" in TrialManager); /enroll is
                            // built by joining trials to children, so it can't
                            // list a trial-less child — the button would dead-end.
                            // convertedPackageId ⇒ paid; the child would then have
                            // a course and this block wouldn't render (bar the
                            // rare Stage-2 partial where payment landed but the
                            // course didn't attach).
                            const canEnrol = !!trial && !trial.convertedPackageId;
                            return (
                              <div className="mt-3">
                                {trial ? (
                                  <>
                                    <span className="inline-flex px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800">
                                      Free trial
                                    </span>
                                    <div className="text-sm text-gray-600 mt-2">
                                      {trialStatusLine(trial)}
                                    </div>
                                  </>
                                ) : (
                                  <div className="text-sm text-gray-600">
                                    {child.name} isn&apos;t enrolled yet. To enrol{' '}
                                    {child.name}, contact us at{' '}
                                    <a href="mailto:info@my-roots.ca" className="text-purple-600 underline font-medium">
                                      info@my-roots.ca
                                    </a>.
                                  </div>
                                )}
                                {canEnrol && (
                                  <button
                                    onClick={() => navigate('/enroll')}
                                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-lg transition-colors"
                                  >
                                    <Crown size={15} /> Enrol {child.name}
                                  </button>
                                )}
                                {trial && (
                                  <button
                                    onClick={() => navigate('/trial-status')}
                                    className="mt-2 block text-sm text-purple-600 hover:text-purple-800 underline font-medium"
                                  >
                                    View trial status
                                  </button>
                                )}
                              </div>
                            );
                          })()}

                          {/* Renewal Message — hidden during child edit */}
                          {!isEditingThisChild && isRenewable && (
                            <div className="mt-3 text-sm text-blue-600 font-medium">
                              {childCourse.status === 'completed'
                                ? '🎉 Course complete — ready for the next course?'
                                : '✓ Eligible for renewal'}
                            </div>
                          )}

                          {/* ── TEMPORARY: Class Link (Zoom) ─────────────────────
                               Remove this block when calendar booking is live.
                          ────────────────────────────────────────────────────── */}
                          {!isEditingThisChild && child.classLink && (
                            <div className="mt-4 border-t border-gray-200 pt-4">
                              <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                <Link size={16} className="text-blue-500" />
                                Your Class Link
                              </h4>
                              <div className="space-y-2">
                                {/* Big join button */}
                                <a
                                  href={child.classLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl transition-colors"
                                >
                                  <ExternalLink size={18} />
                                  Join Class
                                </a>
                                {/* Copyable link row */}
                                <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                                  <span className="flex-1 text-xs text-gray-500 truncate">
                                    {child.classLink}
                                  </span>
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(child.classLink);
                                      setCopiedLinkId(child.id);
                                      setTimeout(() => setCopiedLinkId(null), 2000);
                                    }}
                                    className="flex-shrink-0 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                                  >
                                    {copiedLinkId === child.id ? (
                                      <><CheckCircle size={13} className="text-green-500" /> Copied!</>
                                    ) : (
                                      <><Copy size={13} /> Copy</>
                                    )}
                                  </button>
                                </div>
                                {/* Meeting ID / passcode — read-only display */}
                                {child.classLinkMeetingId && (
                                  <p className="text-xs text-gray-400">Meeting ID: {child.classLinkMeetingId}</p>
                                )}
                                {child.classLinkPasscode && (
                                  <p className="text-xs text-gray-400">Passcode: {child.classLinkPasscode}</p>
                                )}
                              </div>
                            </div>
                          )}

                          {/* ✅ Report Cards Section - Parent View */}
                          {!isEditingThisChild && child.reportCards && child.reportCards.length > 0 && (
                            <div className="mt-4 border-t border-gray-200 pt-4">
                              <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                <FileText size={16} className="text-purple-600" />
                                Report Cards ({child.reportCards.length})
                              </h4>
                              <div className="space-y-2">
                                {child.reportCards
                                  .sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt))
                                  .map((rc, idx) => (
                                    <div
                                      key={idx}
                                      className="flex items-center justify-between p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                                    >
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium text-gray-800 text-sm">
                                            {rc.reportTitle}
                                          </span>
                                          {!rc.viewedAt && (
                                            <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                                              NEW
                                            </span>
                                          )}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                          {new Date(rc.uploadedAt).toLocaleDateString('en-US', {
                                            month: 'long',
                                            day: 'numeric',
                                            year: 'numeric'
                                          })}
                                        </div>
                                        {rc.notes && rc.notes.trim() && (
                                          <div className="text-sm text-gray-600 mt-2 italic">
                                            "{rc.notes}"
                                          </div>
                                        )}
                                      </div>
                                      <button
                                        onClick={() => handleDownloadReport(child.id, rc.id, rc.fileUrl)}
                                        className="ml-3 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium flex items-center gap-2"
                                      >
                                        <FileText size={16} />
                                        View
                                      </button>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Renewal Action Buttons */}
                  {getRenewableChildren(userProfile).length > 0 && (
                    <div className="mt-6 flex flex-wrap gap-3">
                      {/* Renew Selected Button */}
                      {selectedChildIds.length > 0 && (
                        <button
                          onClick={() => navigate('/course-renewal', { 
                            state: { childIds: selectedChildIds }
                          })}
                          className="flex-1 min-w-[200px] px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-200 flex items-center justify-center gap-2"
                        >
                          <RefreshCw size={20} />
                          Renew Selected ({selectedChildIds.length})
                        </button>
                      )}
                      
                      {/* Renew All Button */}
                      {getRenewableChildren(userProfile).length > 1 && (
                        <button
                          onClick={() => {
                            const allRenewableIds = getRenewableChildren(userProfile).map(c => c.id);
                            navigate('/course-renewal', { 
                              state: { childIds: allRenewableIds }
                            });
                          }}
                          className="flex-1 min-w-[200px] px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center justify-center gap-2"
                        >
                          <RefreshCw size={20} />
                          Renew All ({getRenewableChildren(userProfile).length})
                        </button>
                      )}
                      
                      {/* Single Child Renew Button */}
                      {getRenewableChildren(userProfile).length === 1 && selectedChildIds.length === 0 && (
                        <button
                          onClick={() => {
                            const child = getRenewableChildren(userProfile)[0];
                            navigate('/course-renewal', { 
                              state: { childIds: [child.id] }
                            });
                          }}
                          className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-200 flex items-center justify-center gap-2"
                        >
                          <RefreshCw size={20} />
                          Renew Course
                        </button>
                      )}
                    </div>
                  )}
                  
                  {/* Add Children Button — hidden for adult single-learner accounts */}
                  {!isAdultAccount(userProfile) && (
                    <div className="mt-4">
                      <button
                        onClick={() => navigate('/add-children')}
                        className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white font-bold rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-200 flex items-center justify-center gap-2"
                      >
                        <Users size={20} />
                        Enroll Additional Children
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Payment & Actions */}
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl">
                <CreditCard size={24} />
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Payment & Billing</h2>
            </div>

            <div className="space-y-4">
              {/* Payment Method Section */}
              <div className="border-t border-gray-200 pt-4 mt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <CreditCard size={20} className="text-gray-600" />
                  Payment Method
                </h3>
                
                {userProfile?.payment?.savedPaymentMethodId ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                      <CheckCircle className="text-green-600" size={16} />
                      <div>
                        <div className="font-medium text-gray-800 text-sm">Card Saved</div>
                        <div className="text-xs text-gray-600">Ready for easy renewals</div>
                      </div>
                    </div>
                    
                    <button
                      onClick={handleRemovePaymentMethod}
                      disabled={isProcessing}
                      className="w-full bg-red-50 hover:bg-red-100 text-red-600 py-2 px-3 rounded-lg font-medium transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                    >
                      {isProcessing ? (
                        <div className="w-5 h-5 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <>
                          <Trash2 size={16} />
                          Remove Saved Card
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <XCircle className="text-gray-400" size={16} />
                      <div className="text-sm text-gray-600">
                        No payment method saved. You can save one during renewal.
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Manage Billing Button */}
              {userProfile?.payment?.stripeCustomerId && (
                <button
                  onClick={handleManageBilling}
                  disabled={isProcessing}
                  className="w-full bg-indigo-500 hover:bg-indigo-600 text-white font-medium py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isProcessing ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <Settings size={20} />
                      Manage Billing
                      <ExternalLink size={16} />
                    </>
                  )}
                </button>
              )}

              {/* Refresh Button */}
              <button
                onClick={loadUserProfile}
                disabled={isProcessing}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <RefreshCw size={20} className={isProcessing ? 'animate-spin' : ''} />
                Refresh Account Data
              </button>

              {/* Contact Support */}
              <button
                onClick={() => window.location.href = 'mailto:admin@my-roots.ca'}
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-2"
              >
                <Mail size={20} />
                Contact Support
              </button>

              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="w-full bg-red-100 hover:bg-red-200 text-red-700 font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 border border-red-300"
              >
                <LogOut size={20} />
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="text-red-500" size={20} />
              <div className="text-red-800">{error}</div>
            </div>
          </div>
        )}

        {successMessage && (
          <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="text-green-500" size={20} />
              <div className="text-green-800">{successMessage}</div>
            </div>
          </div>
        )}

        {/* Help Text */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="text-blue-800 text-sm">
            <strong>Need help?</strong> You can renew your course at any time. 
            If you have a saved payment method, renewal is quick and easy. For billing questions, 
            use the "Manage Billing" button or contact our support team.
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccountSettings;